/**
 * ConfirmDialog — glassmorphism-styled confirmation dialog.
 *
 * Replaces native window.confirm() for theme consistency.
 * Renders at z-1300/z-1400 (above modals at z-1100/z-1200) with backdrop click and Escape to cancel.
 * Optional anchorY positions the dialog near a specific vertical offset.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  confirmColor?: string;
  /** Vertical offset (px) from container top to anchor the dialog near. */
  anchorY?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Delete',
  confirmColor = '#ef4444',
  anchorY,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onConfirm, onCancel]);

  // Position: anchored near a row, or centered if no anchor
  const positionStyle = anchorY != null
    ? { top: anchorY, left: '50%', transform: 'translateX(-50%)' }
    : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[1300]" style={{ background: 'var(--modal-backdrop)' }} onClick={onCancel} />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed z-[1400] w-80 rounded-2xl"
        style={{
          ...positionStyle,
          background: 'var(--modal-bg-heavy)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid ${confirmColor}35`,
          boxShadow: `var(--modal-shadow), 0 0 40px ${confirmColor}15`,
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 p-4 pb-3" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <AlertTriangle size={16} style={{ color: confirmColor }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h3>
        </div>

        {/* Body */}
        <div className="p-4">
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            {message}
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
          <button
            onClick={onCancel}
            className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-secondary)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
            style={{
              background: `${confirmColor}20`,
              border: `1px solid ${confirmColor}55`,
              color: confirmColor,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
